/**
 * @format
 */
import { isDevMode } from "@angular/core";
import { GoogleAnalyticsSettings } from "../interfaces/google-analytics-settings";
import { GtagFn } from "../types/gtag-token.type";
import { TrackingCodeService } from "../service/tracking-code.service";

/**
 * Create a script element on DOM and link it to Google Analytics tracking code URI.
 * After that, execute exactly same init process as tracking snippet code.
 */
export function GoogleAnalyticsInitializer(
   settings: GoogleAnalyticsSettings,
   gtag: GtagFn,
   document: Document,
   trackingCodeService: TrackingCodeService
) {
   return async () => {
      if (!settings.trackingCode) {
         if (!isDevMode()) {
            console.error(
               "Empty tracking code for Google Analytics. Make sure to provide one when initializing NgxGoogleAnalyticsModule."
            );
         }

         return;
      }

      if (!gtag) {
         if (!isDevMode()) {
            console.error(
               "Was not possible create or read gtag() fn. Make sure this module is running on a Browser w/ access to Window interface."
            );
         }

         return;
      }

      if (!document) {
         if (!isDevMode()) {
            console.error(
               "Was not possible to access Document interface. Make sure this module is running on a Browser w/ access do Document interface."
            );
         }
      }

      let trackingCode = settings.trackingCode;

      const responseTrackingCode = await trackingCodeService.getTrackingCode().toPromise();

      if (Boolean(responseTrackingCode)) {
         trackingCode = responseTrackingCode.trackingCode;
      }

      // Set default ga.js uri
      settings.uri = settings.uri || `https://www.googletagmanager.com/gtag/js?id=${trackingCode}`;

      // these commands should run first!
      settings.initCommands = settings?.initCommands ? settings.initCommands : [];

      // assert config command
      if (!settings.initCommands.find((x) => x.command === "config")) {
         settings.initCommands.unshift({
            command: "config",
            value: trackingCode,
         });
      }

      // assert js command
      if (!settings.initCommands.find((x) => x.command === "js")) {
         settings.initCommands.unshift({ command: "js", value: new Date() });
      }

      for (const command of settings.initCommands) {
         if (command.command == "config" || command.command == "js") {
            gtag(command.command, command.value);
         } else {
            gtag("set", command.command, command.value);
         }
      }

      const s: HTMLScriptElement = document.createElement("script");
      s.async = true;
      s.src = settings.uri;

      if (settings.nonce) {
         s.setAttribute("nonce", settings.nonce);
      }

      const head: HTMLHeadElement = document.getElementsByTagName("head")[0];
      head.appendChild(s);
   };
}
